import { Box, Card, Link, Typography } from '@mui/material'
import dayjs from 'dayjs'
import {
  CourseV2Accreditation,
  CourseV2Content,
} from 'src/v3/classroom-v2/types'
import {
  CardHeading,
  formatHours,
} from 'src/v3/classroom-v2/components/course-overview'

let Subheading = ({ children }: { children: string }) => (
  <Typography variant="subtitle2" component="h3" sx={{ mb: 0.5 }}>
    {children}
  </Typography>
)

let BulletList = ({ items }: { items: string[] }) => (
  <Box component="ul" sx={{ m: 0, pl: 2.5, listStyleType: 'disc' }}>
    {items.map((item) => (
      <Typography
        key={item}
        component="li"
        variant="body2"
        color="text.secondary"
      >
        {item}
      </Typography>
    ))}
  </Box>
)

export let AccreditationStatements = ({
  content,
}: {
  content: CourseV2Content
}) => {
  let capce = content.accreditation.find(
    (a): a is Extract<CourseV2Accreditation, { kind: 'capce' }> =>
      a.kind === 'capce',
  )
  let ancc = content.accreditation.find(
    (a): a is Extract<CourseV2Accreditation, { kind: 'ancc' }> =>
      a.kind === 'ancc',
  )
  let accreditationBullets = [
    capce
      ? `This CE activity is accredited by the Commission of Accreditation for Prehospital Continuing Education for ${formatHours(content.length)} Based/Advanced CEH.`
      : null,
    ancc
      ? "FOAMfrat is accredited as a provider of nursing continuing professional development by the American Nurses Credentialing Center's Commission on Accreditation."
      : null,
  ].filter((bullet): bullet is string => bullet !== null)
  let criteriaBullets = [
    'Attendance for 100% of the activity',
    content.quiz != null ? 'Successful completion of a post-course quiz' : null,
    'Completion and submission of post-course evaluation form',
  ].filter((bullet): bullet is string => bullet !== null)

  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <CardHeading>Accreditation Statements</CardHeading>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {accreditationBullets.length ? (
          <BulletList items={accreditationBullets} />
        ) : null}

        <Box>
          <Subheading>Criteria for Receiving Contact Hours</Subheading>
          <BulletList items={criteriaBullets} />
        </Box>

        {ancc ? (
          <Box>
            <Subheading>ANCC Expiration Date</Subheading>
            <Typography variant="body2" color="text.secondary">
              {dayjs(ancc.expirationDate).format('MMMM DD, YYYY')}
            </Typography>
          </Box>
        ) : null}

        <Box>
          <Subheading>Relationship Disclosure</Subheading>
          <Typography variant="body2" color="text.secondary">
            None of the planners or presenters for this activity have relevant
            financial relationship(s) to disclose with ineligible companies.
          </Typography>
        </Box>

        <Box>
          <Subheading>Contact Information</Subheading>
          <Typography variant="body2" color="text.secondary">
            For inquiries or support, please contact FOAMfrat LLC at{' '}
            <Link href="mailto:support@foamfrat.com">support@foamfrat.com</Link>{' '}
            or visit our website at{' '}
            <Link
              href="http://www.foamfrat.com"
              target="_blank"
              rel="noopener noreferrer"
            >
              www.foamfrat.com
            </Link>
            .
          </Typography>
        </Box>

        <Box>
          <Subheading>Educational Use Disclaimer</Subheading>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            This material is for educational purposes only and is not a
            standalone guide for patient care. It does not replace your local
            protocols, standing orders, or medical direction. Practices,
            medications, and scope of practice vary by region and agency, so
            always follow the protocols approved for your system.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Nothing here substitutes for clinical judgment. Every patient is
            different, and providers are responsible for individual assessment
            and for seeking medical control when appropriate. Medical guidance
            changes over time, so verify information against current protocols
            and authoritative sources. Responsibility for patient care rests
            with the treating provider and their medical direction.
          </Typography>
        </Box>
      </Box>
    </Card>
  )
}
